<svelte:options
	customElement={{
		tag: 'rf-text',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	/**
	 * Types
	 */

	type ReframeTextType = 'title' | 'description' | 'source' | 'text';

	export interface ReframeTextProps {
		tag: string;
		type: ReframeTextType;
		appear: boolean;
		'appear-trigger': string;
		'appear-once': boolean;
	}

	export interface ReframeTextComponent extends HTMLElement, ReframeTextProps {
		tagName: 'RF-TEXT';
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import { useAppear } from '@src/composables/useAppear.svelte';
	import parseBool from '@src/utils/bool';

	/**
	 * Props
	 */
	let {
		tag = 'span',
		type = 'text',
		appear = false,
		'appear-trigger': appearTrigger = '20%',
		'appear-once': appearOnce = true,
	}: ReframeTextProps = $props();

	/**
	 * Composables
	 */

	const apparition = useAppear({
		once: parseBool(appearOnce),
		trigger: appearTrigger,
	});

	/**
	 * Derived
	 */
	const classes = $derived([
		'rf-text',
		`rf-text--${type}`,
		{
			'rf-text--appear': parseBool(appear),
			'rf-text--appear-visible': appear ? apparition.isVisible : false,
		},
	]);
</script>

<svelte:element this={tag} bind:this={apparition.element} class={classes} part="rf-text">
	<slot></slot>
</svelte:element>

<style lang="scss" global>
	.rf-text {
		margin: 0;
		&--appear {
			opacity: 0;
			transition: var(--rf-text-transition, 1s ease opacity);

			&-visible {
				opacity: 1;
			}
		}

		::slotted(a) {
			color: inherit;
		}
	}

	h1 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h1-font-size, 64px);
	}
	h2 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h2-font-size, 54px);
	}
	h3 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h3-font-size, 48px);
	}
	h4 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h4-font-size, 32px);
	}
	h5 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h5-font-size, 24px);
	}
	h6 {
		font-family: var(--rf-headings-font);
		font-size: var(--rf-h6-font-size, 18px);
	}

	span,
	p {
		line-height: var(--rf-body-line-height, 1);
		font-family: var(--rf-body-font);
		font-size: var(--rf-body-font-size, 18px);
	}
</style>
